<template>
<header
	data-component="DesktopNavbar.vue"
	class="global-navigation | sticky top-0 left-0 right-0 z-48 bg-white border-b border-black/10"
	style="z-index: 65;"
>
	<div class="container mx-auto flex h-20 items-center px-3">
		<!-- Logo Oferteo -->
		<a v-if="!hideLogo" href="/" class="mr-10" data-test="oferteo_logo" @click.prevent="navbarTravel({ travelTo: 'HomePage' })">
			<OferteoLogo/>
		</a>

		<!-- Pozycje głównego menu nawigacyjnego -->
		<template v-if="menu">
			<template v-for="menuItem in navbarMenu" :key="menuItem.travelTo">
				<a
					v-if="!menuItem.hideDesktop"
					:href="menuItem.href ?? ('/app/dist/index.html#' + menuItem.travelTo)"
					class="mr-6 flex h-20 items-center border-b-2 border-transparent transition-all hover:text-accent"
					:class="{
						'!border-accent font-bold !text-accent': menuItem.isActive,
					}"
					:data-test="`global_navigation__${menuItem.travelTo}`"
					@click.prevent="navbarTravel(menuItem)"
				>
					{{ menuItem.label }}
					<div
						v-if="menuItem.badge && menuItem.badgeCount"
						class="navigation-counter | ml-1 min-w-[1.125rem] rounded-full bg-info px-1 py-px text-center text-xs font-extrabold leading-4 text-white"
						v-html="menuItem.badgeCount"
					/>
				</a>
			</template>
		</template>

		<!-- Separator -->
		<div class="flex-auto"/>

		<!-- Pozycje menu sprzedawcy w Moim Oferteo -->
		<template v-if="CurrentModule === 'CustomerView'">
			<!-- Stan konta -->
			<button
				class="btn btn-small mr-10 px-4"
				:class="rechargeButtonClass"
				data-test="acount-balance_btn"
				@click="navbarTravel({ travelTo: 'Settings', icon: '', label: 'Doładuj konto' })"
				v-html="rechargeButtonLabel"
			/>

			<!-- Powiadomienia -->
			<DropdownPanel class="global-notifications | mr-6" title="Powiadomienia" Direction="bottom right">
				<template #button>
					<div class="relative top-1 flex text-neutral-600 hover:text-accent">
						<OIcon name="bell" size="1.25" data-test="notification_bell"/>
						<div v-if="promoMessagesCount" class="absolute right-0 top-0 -m-1 size-2 rounded bg-info"/>
					</div>
				</template>
				<PromoMessages/>
			</DropdownPanel>

			<!-- Terminarz -->
			<button class="mr-10 text-neutral-600 hover:text-accent" @click="navbarTravel({label: 'Terminarz', travelTo:'SchedulerWrapper'})">
				<OIcon name="calendar-list" size="1.25" data-test="timetable"/>
			</button>
		</template>

		<!-- Menu zalogowanego użytkownika -->
		<MenuNavbar v-if="firstName || isCustomer"/>

		<!-- Menu niezalogowanego użytkownika -->
		<div v-else>
			<a v-if="CurrentState.LoginPage === 'login'" rel="nofollow" class="inline-block text-sm font-semibold uppercase text-neutral-900" :href="'/rejestracja/wybierz-klientow'">Załóż konto {{ ScreenSize > 1 ? 'wykonawcy' : '' }}</a>
			<a v-else rel="nofollow" class="inline-block text-sm font-semibold uppercase text-neutral-900" :href="'/login.ofer'">Zaloguj się {{ ScreenSize > 1 ? 'jako wykonawca' : '' }}</a>
		</div>
	</div>
</header>
</template>

<script lang="ts">
import { mapState, mapWritableState, mapStores, mapActions } from 'pinia';
import { PropType } from 'vue';
import DropdownPanel from '@globals/components/global/DropdownPanel.vue';
import { useAppStore } from '@globals/stores/app';
import { useNotificationCountersStore } from '@globals/stores/notificationCounters';
import { useUserStore } from '@globals/stores/user';
import NavbarItem from '@globals/types/components/NavbarItem';
import PromoMessages from '@customer/global/PromoMessages.vue';
import { useCustomerProfileViewStore } from '@customer/stores/customerProfileView';
import { useCustomerRfpListStore } from '@customer/stores/customerRfpList';
import { usePromoMessagesStore } from '@customer/stores/promoMessages';
import { useServicesStore } from '@customer/stores/services';
import MenuNavbar from './MenuNavbar.vue';

/**
 * Komponent odpowiedzialny za wyświetlanie górnego paska nawigacyjnego w wersji desktopowej.
 */
export default {
	name: 'DesktopNavbar',
	components: {
		DropdownPanel,
		PromoMessages,
		MenuNavbar,
	},
	props: {
		/**
		 * Elementy do pokazania na pasku nawigacyjnym.
		 */
		menu: {
			type: Array as PropType<NavbarItem[]>,
			required: false,
		},
		/**
		 * Elementy do pokazania na pasku nawigacyjnym.
		 */
		forceSelect: {
			type: String,
			required: false,
		},
		/**
		 * Czy ukryć logo?
		 */
		hideLogo: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			stateManagerToVueRouter: {
				Profile: 'CustomerProfile',
				Services: 'Services',
				SchedulerWrapper: 'SchedulerWrapper',
				Rfps: 'Rfps',
				Contacts: 'Contacts',
			} as Record<string, string>,
		};
	},
	computed: {
		...mapStores(useNotificationCountersStore),
		...mapStores(useCustomerRfpListStore),
		...mapState(useUserStore, ['accountBalance', 'maxCategoryPrice', 'isCustomer', 'firstName', 'onboardingFreeUncoversLeft']),
		...mapState(useServicesStore, [
			'isFilterDirty',
		]),
		...mapState(useCustomerRfpListStore, [
			'fromListType',
			'selectedListType',
			'isPublicView',
		]),
		...mapWritableState(useServicesStore, [
			'displaySaveChanges',
		]),
		...mapWritableState(useAppStore, [
			'nextRouteNameAttempt',
		]),
		...mapWritableState(useCustomerProfileViewStore, [
			'isVueRouterView',
			'isFormChanged',
			'isDataShouldSubmit',
		]),
		isPublicRfp() {
			return this.customerRfpListStore.isPublicView;
		},

		/**
		 * Zwraca klasę CSS przycisku doładowania konta.
		 */
		rechargeButtonClass() {
			if (this.hasFlag('blackWeek')) {
				return 'text-white bg-neutral-900 hover:bg-neutral-800';
			}
			return this.accountBalance ? 'btn-outline-accent' : 'btn-accent';
		},
		/**
		 * Zwraca etykietę przycisku doładowania konta. Jeżeli jest obięty promocją, pokazuje "Promocja".
		 * W innym przypadku, gdy użytkownik ma wystarczająco dużo punktów, to zwraca ich ilość.
		 * Jeżeli ilość jego punktów jest mniejsza niż najwyższa cena kategorii, to zwraca "Doładuj konto".
		 */
		rechargeButtonLabel() {
			if (this.hasFlag('blackWeek')) {
				return 'Promocja Black Week';
			}
			if (this.onboardingFreeUncoversLeft) {
				return this.Format.PluralForm(this.onboardingFreeUncoversLeft, ['odkrycie', 'odkrycia', 'odkryć'], true);
			}
			if (this.hasFlag('hasPromoPacket')) {
				return 'Promocja!';
			}
			return this.accountBalance > this.maxCategoryPrice ? `${parseFloat(this.accountBalance.toFixed(2))} pkt` : 'Doładuj konto';
		},
		/**
		 * Zwraca główne menu nawigacyjne z dodanymi statusami aktywności.
		 */
		navbarMenu() {
			return this.menu?.map((item) => {
				const isStateOrSectionName = this.State?.CurrentStateName === item.travelTo || this.CurrentState?.Section === item.travelTo;
				const isActiveWhen = item.activeWhen && item.activeWhen.indexOf(this.State?.CurrentStateName) > -1;


				if (this.isVueRouterView) {
					const routeName = this.stateManagerToVueRouter[item.travelTo];

					item.isActive = false;

					if (this.$route.name === 'RfpDetailsList') {
						item.isActive = (item.travelTo === this.fromListType);
					} else if (this.$route.name === 'ContactsFilters' || this.$route.name === 'RfpsFilters') {
						item.isActive = (item.travelTo === this.selectedListType);
					} else {
						item.isActive = this.$route.matched.some((activeRoute) => activeRoute.name === routeName);
					}
				} else if (this.forceSelect) {
					item.isActive = this.forceSelect === item.travelTo;
				} else {
					if (this.isPublicRfp === true) {
						item.isActive = (item.travelTo === 'Rfps');
					} else {
						item.isActive = isStateOrSectionName || isActiveWhen;
					}
				}

				const badgeCount = (item.badge && this.notificationCountersStore[item.badge]) ? this.notificationCountersStore[item.badge] : 0;
				item.badgeCount = badgeCount > 99 ? '99+' : badgeCount;

				return item;
			});
		},

		/**
		 * Zwraca liczbę powiadomień.
		 */
		...mapState(usePromoMessagesStore, ['promoMessagesCount']),
	},
	methods: {
		...mapActions(useAppStore, ['hasFlag']),

		/**
		 * Przenosi użytkownika na wskazaną stronę.
		 */
		async navbarTravel(item: NavbarItem) {
			if (item.label === 'Doładuj konto' && this.hasFlag('blackWeek')) {
				document.location.href = '/konto/ustawienia?pricingModal=true';
			}

			if (this.Analytics) {
				this.Analytics.sendGAEvent('navbar_click', { label: item.label });
			}

			// Przejdź pod podany adres URL, o ile został podany...
			if (item.url) {
				document.location.href = item.url;
				return;
			}

			// Przenieś na stronę główną...
			if (item.travelTo === 'HomePage') {
				document.location.href = '/';
				return;
			}

			// Na adminie jest w pełni Vue Router...
			if (this.CurrentModule === 'Admin') {
				this.$router.push({ name: item.travelTo });

				return;
			}

			// tutaj sprawdzamy czy ktoś ma nie zapisane zmiany i czy chce przejść na inną stonę która nie jest jeszcze w vue routerze
			if (this.isFormChanged || this.isFilterDirty) {
				if ((this.$route.name as string)?.startsWith('CustomerProfile')) {
					this.isDataShouldSubmit = true;
				} else {
					this.displaySaveChanges = true;
				}

				this.nextRouteNameAttempt = item.travelTo === 'Profile' ? 'CustomerProfileBasicInfo' : item.travelTo;

				return;
			}

			// Strony korzystające z Vue Router-a...
			if (item.travelTo in this.stateManagerToVueRouter) {
				const routeName = this.stateManagerToVueRouter[item.travelTo];

				if (!routeName) {
					console.error('Unknown route name for travelTo', item.travelTo);

					return;
				}
				// Jeżeli to pierwsze wejście na stronę z Vue Routerem, to użyj replace
				await this.$router.push({ name: routeName, replace: !this.isVueRouterView, force: !this.isVueRouterView });
				this.isVueRouterView = true;

				return;
			}

			this.isVueRouterView = false;

			// ...w przeciwnym razie użyj State.Travel
			this.State.Travel(item.travelTo);
		},
	},
};

</script>

<style lang="scss">
.global-notifications .dropdown-panel {
	top: 2.5rem;
	width: 37.5rem;
	padding: 0;
	margin-right: -8rem;
}
</style>
